import type { MapId } from './maps'

export interface CampaignTransmission {
  readonly speaker: string
  readonly channel: string
  readonly lines: readonly string[]
}

export interface CampaignChapter {
  readonly mapId: MapId
  readonly act: string
  readonly title: string
  readonly codename: string
  readonly objective: string
  readonly prelude: CampaignTransmission
  readonly debrief: CampaignTransmission
  readonly rewardXp: number
  readonly reserveBonus: number
  readonly scoreBonus: number
}

export const CAMPAIGN_CHAPTERS: Record<MapId, CampaignChapter> = {
  GENESIS_NETWORK: {
    mapId: 'GENESIS_NETWORK', act: 'ACT I', title: 'THE FIRST BLOCK', codename: 'ORIGIN SIGNAL',
    objective: 'Restore the Genesis routing lattice and protect the original ledger Core.',
    prelude: {
      speaker: 'ARCHIVIST // NODE 00', channel: 'ENCRYPTED GENESIS CHANNEL',
      lines: [
        'The first block is still alive, but hostile processes are rewriting its routes.',
        'Rebuild the defense lattice. If Genesis falls, every sector loses its source of truth.',
      ],
    },
    debrief: {
      speaker: 'ARCHIVIST // NODE 00', channel: 'LEDGER RECOVERY',
      lines: [
        'Genesis is immutable again. The recovered signatures point toward a coordinated market assault.',
        'Follow the capital flow into Neon Exchange. The attack did not begin here.',
      ],
    },
    rewardXp: 180, reserveBonus: 250, scoreBonus: 600,
  },
  NEON_EXCHANGE: {
    mapId: 'NEON_EXCHANGE', act: 'ACT II', title: 'MARKET GHOSTS', codename: 'LIQUIDITY TRACE',
    objective: 'Survive the engineered panic and identify the source behind the synthetic sell cascade.',
    prelude: {
      speaker: 'BROKER // GHOST DESK', channel: 'DARK-POOL RELAY',
      lines: [
        'Every sell order is arriving before the signal that created it. Someone is trading against the future.',
        'Hold the Exchange long enough to trace the phantom liquidity back to its validator set.',
      ],
    },
    debrief: {
      speaker: 'BROKER // GHOST DESK', channel: 'TRACE CONFIRMED',
      lines: [
        'The cascade is broken. Its settlement proofs all converge above the Validator Heights.',
        'This was never a market attack. It was cover for a consensus breach.',
      ],
    },
    rewardXp: 260, reserveBonus: 350, scoreBonus: 900,
  },
  VALIDATOR_HEIGHTS: {
    mapId: 'VALIDATOR_HEIGHTS', act: 'ACT III', title: 'FINALITY WAR', codename: 'LAST CONSENSUS',
    objective: 'Defend both uplinks, defeat the Finality Breaker, and restore network consensus.',
    prelude: {
      speaker: 'VALIDATOR PRIME', channel: 'BYZANTINE EMERGENCY LINK',
      lines: [
        'The hostile fork controls half the Heights. Both uplinks must remain synchronized.',
        'Destroy the Finality Breaker before it certifies the false chain as history.',
      ],
    },
    debrief: {
      speaker: 'VALIDATOR PRIME', channel: 'CONSENSUS RESTORED',
      lines: [
        'The false chain has collapsed. Genesis, the Exchange, and the Heights agree once more.',
        'The network is secure—for now. Your defense record has been committed to the permanent ledger.',
      ],
    },
    rewardXp: 400, reserveBonus: 500, scoreBonus: 1_400,
  },
}

export const CAMPAIGN_ORDER: readonly MapId[] = ['GENESIS_NETWORK', 'NEON_EXCHANGE', 'VALIDATOR_HEIGHTS']

export function campaignChapter(mapId: MapId): CampaignChapter {
  return CAMPAIGN_CHAPTERS[mapId]
}
